import bindAllOfThis from "src/utils/BindThisHelper";
import { PhaserButtonConfig, PhaserButton } from "src/games/shared/Button";
import { Color } from "src/games/shared/ColorValue";
import { TiledGameObject } from "src/games/shared/phaserHelpers/HandyTypes";
import { GameInstructionsTextBox, InstructionsTextBoxConfig } from "src/games/shared/GameInstructionsTextBox";
import TranslateApi from "src/utils/TranslateHelper";
import AudioTranslations from "src/constants/AudioTranslations";
import { GameIntroBaseScene } from "src/games/shared/scenes/GameIntroBase";
import { Games } from "src/constants/Games";
import { SceneObject } from "src/games/shared/SceneObject";
import ConfigUtil from "src/utils/ConfigUtil";

export class HowToPlaySceneBase extends Phaser.Scene {
    gameWidth: number;
    gameHeight: number;
    background: Phaser.GameObjects.Image;
    map: Phaser.Tilemaps.Tilemap;

    keyboardInstructionsBox: GameInstructionsTextBox;
    keyboardButton: PhaserButton;

    skyColor: number = 0x6eb8e5;
    textBox: GameInstructionsTextBox;

    isTouchDisplayed: boolean = false;

    // To be filled by child class
    gameRef = Games.Default;
    mainScene: typeof Phaser.Scene | SceneObject = null;
    gameIntroScene: typeof GameIntroBaseScene = null;
    gameObjectiveTextKey: string = "";
    howToPlayOnComputerTextKey: string = "";
    howToPlayOnTabletTextKey: string = "";

    constructor() {
        super({
            key: "HowToPlay"
        });
        bindAllOfThis(this, HowToPlaySceneBase.prototype);
    }

    preload(): void {
        // This is a "generic" base class and we should sanity check the required conditions are met before proceeding
        if (this.gameRef == null) { throw new Error("HowToPlaySceneBase: GameRef has not been initialized"); }
        if (this.mainScene == null) { throw new Error("HowToPlaySceneBase: MainScene has not been initialized"); }
        // This was changed as a few games have no intro scene
        if (this.gameIntroScene == null) { console.info("GameIntroScene is null. Assuming no intro scene"); }
        if (this.gameObjectiveTextKey == null) { throw new Error("HowToPlaySceneBase: GameObjectiveTextKey has not been initialized"); }
        if (this.howToPlayOnComputerTextKey == null) { throw new Error("HowToPlaySceneBase: HowToPlayOnComputerTextKey has not been initialized"); }
        if (this.howToPlayOnTabletTextKey == null) { throw new Error("HowToPlaySceneBase: HowToPlayOnTabletTextKey has not been initialized"); }

        // Catch load errors here
        this.load.on("loaderror", (file: Phaser.Loader.File) => {
            console.error(`File load error: ${file.key}`);
            // Audio file missed, load blank audio as it *should* always exist
            if (file.type === "audio") {
                this.load.audio(file.key, require("/src/assets/qci/sounds/blank_audio.mp3"));
            }
        });

        this.load.tilemapTiledJSON('intro-map', require("/src/assets/qci/maps/intro_scene.json"));
        this.load.atlas("pilots", require("/src/assets/qci/atlas/starPilotAll.png"), require("/src/assets/qci/atlas/starPilotAll.json"));
        this.load.atlas("starPods", require("/src/assets/qci/atlas/starPodAll.png"), require("/src/assets/qci/atlas/starPodAll.json"));

        this.load.audio(this.gameObjectiveTextKey, AudioTranslations.FindByKeyAndLanguage(this.gameObjectiveTextKey, TranslateApi.currentLanguage.code).url);
        this.load.audio(this.howToPlayOnComputerTextKey, AudioTranslations.FindByKeyAndLanguage(this.howToPlayOnComputerTextKey, TranslateApi.currentLanguage.code).url);
        // We dont always have tablet instructions
        if (this.howToPlayOnTabletTextKey.length > 1) {
            this.load.audio(this.howToPlayOnTabletTextKey, AudioTranslations.FindByKeyAndLanguage(this.howToPlayOnTabletTextKey, TranslateApi.currentLanguage.code).url);
        }
    }

    create(): void {
        this.gameWidth = this.textures.game.config.width as number;
        this.gameHeight = this.textures.game.config.height as number;
        this.map = this.make.tilemap({ key: "intro-map" });

        let sky = this.add.image(0, 0, "pixel");
        sky.setOrigin(0);
        sky.setDisplaySize(this.gameWidth, this.gameHeight);
        sky.setTint(this.skyColor, this.skyColor, 0xffffff, 0xffffff); // Gradient sky!

        // Set touch flag
        this.isTouchDisplayed = this.sys.game.device.input.touch && this.howToPlayOnTabletTextKey.length > 1;

        // Sound management
        this.runSoundEngine();

        this.map.getObjectLayer("Objects").objects.forEach((ob: TiledGameObject) => {
            if (ob.name === "text") {
                var translationKeys = [this.gameObjectiveTextKey];
                if (this.isTouchDisplayed) {
                    // Touch device: defaults to show tablet instructions, but also show a button to show them the keyboard instructions
                    translationKeys.push(this.howToPlayOnTabletTextKey);
                    this.addKeyboardInstructionsButton(ob);
                }
                else {
                    translationKeys.push(this.howToPlayOnComputerTextKey);
                }

                var config = {
                    padding: 20,
                    fontSize: 20,
                    hollandCode: this.gameRef.planet.name.toLowerCase(),
                    translationKeys: translationKeys,
                    onBack: this.back,
                    onStartGame: this.startGame,
                } as InstructionsTextBoxConfig;

                this.textBox = new GameInstructionsTextBox(this, ob, config);

                // If there is no previous scene, we should not be able to go back
                if (this.gameIntroScene == null) {
                    this.textBox.backButton.setVisible(false);
                    this.textBox.backButton.setEnabled(false);
                }
            }
            else if (ob.name === "pod") {
                var starPod = this.add.image(ob.x, ob.y, "starPods", this.gameRef.planet.name.toLowerCase());
                starPod.setOrigin(0);
                starPod.setDepth(2);
                starPod.setDisplaySize(ob.width, ob.height);
            }
            else if (ob.name === "pilot") {
                var starPilot = this.add.image(ob.x, ob.y, "pilots", this.gameRef.planet.name.toLowerCase());
                starPilot.setOrigin(0);
                starPilot.setDisplaySize(ob.width, ob.height);
                starPilot.setDepth(3);
            }
            else if (ob.name === "terrain") {
                let terrain = this.add.image(ob.x, ob.y, "terrain");
                terrain.setScale(0.8);
                terrain.setDepth(0);
                terrain.setOrigin(0);
            }
        });
    }

    runSoundEngine(): void {
        this.sound.stopAll();
        if (!ConfigUtil.AudioEnabled) {
            return;
        }

        var objectiveAudio = this.sound.add(this.gameObjectiveTextKey);
        objectiveAudio.play();
        // Add first audio as delay + 0.25 to give some natural space
        if (this.isTouchDisplayed) {
            var howToPlayAudioTablet = this.sound.add(this.howToPlayOnTabletTextKey);
            howToPlayAudioTablet.play("", {
                delay: objectiveAudio.totalDuration + 0.25
            } as SoundConfig);
        } else {
            var howToPlayAudio = this.sound.add(this.howToPlayOnComputerTextKey);
            howToPlayAudio.play("", {
                delay: objectiveAudio.totalDuration + 0.25
            } as SoundConfig);
        }
    }

    back() {
        if (!this.textBox.backButton.isEnabled()) {
            return;
        }
        this.input.enabled = false;
        this.sound.stopAll();
        this.scene.remove(this);
        GameIntroBaseScene.StartFromEnd = true;
        this.scene.add("GameIntroScene", this.gameIntroScene, true);
    }

    startGame() {
        if (!this.input.enabled) {
            return;
        }
        this.input.enabled = false;
        this.sound.stopAll();
        this.scene.remove(this);
        // JB: instanceof decided to be broken, so back to javascript
        // if (this.mainScene instanceof SceneObject) {
        if ('scene' in this.mainScene) {
            let t = this.mainScene as SceneObject;
            this.scene.add(t.key, t.scene, true);
        } else {
            this.scene.add("GameScene", this.mainScene, true);
        }
    }

    addKeyboardInstructionsButton(ob: TiledGameObject): void {
        var config: PhaserButtonConfig = {
            backgroundColor: Color.KUDER_SECONDARY,
            smartSize: true
        };

        // Button to show keyboard instructions
        this.keyboardButton = new PhaserButton(this, ob.x, 0, 300, 100, TranslateApi.translate("public.keyboardInstructions"), config);
        this.keyboardButton.setDepth(10);
        this.keyboardButton.y = this.gameHeight - this.keyboardButton.height - 50;

        // Hide this textbox & show another one with the keyboard instructions
        this.keyboardButton.on('buttonClicked', () => {
            this.textBox.visible = false;
            this.isTouchDisplayed = false;
            this.runSoundEngine();

            if (this.keyboardInstructionsBox == null) {
                var config = {
                    padding: 20,
                    fontSize: 20,
                    hollandCode: this.gameRef.planet.name.toLowerCase(),
                    translationKeys: [this.gameObjectiveTextKey, this.howToPlayOnComputerTextKey],
                    onBack: this.hideKeyboardInstructions,
                    onStartGame: this.startGame,
                } as InstructionsTextBoxConfig;

                this.keyboardInstructionsBox = new GameInstructionsTextBox(this, ob, config);
            }
            else {
                this.keyboardInstructionsBox.visible = true;
                this.keyboardInstructionsBox.backButton.setEnabled(true);
                this.keyboardInstructionsBox.startGameButton.setEnabled(true);
            }

            this.textBox.backButton.setEnabled(false);
            this.textBox.startGameButton.setEnabled(false);
            this.keyboardButton.visible = false;
        });
    }

    hideKeyboardInstructions() {
        this.textBox.visible = true;
        this.isTouchDisplayed = true;
        this.keyboardInstructionsBox.visible = false;
        this.keyboardButton.visible = true;
        this.keyboardInstructionsBox.backButton.setEnabled(false);
        this.keyboardInstructionsBox.startGameButton.setEnabled(false);
        this.textBox.backButton.setEnabled(true);
        this.textBox.startGameButton.setEnabled(true);
    }
}



// WEBPACK FOOTER //
// ./src/games/shared/scenes/HowToPlaySceneBase.ts